import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { referralService } from '../../services/referralService';
import { masterService } from '../../services/masterService';
import { MasterPageWrapper } from './common/MasterPageWrapper';
import CommonPagination from '../../components/common/CommonPagination';
import usePagination from '../../hooks/usePagination';
import { useAuth } from '../../context/AuthContext';
import './ReferralShareConfigPage.css';

export const ReferralShareConfigPage = ({ showToast }) => {
  const { user } = useAuth();
  // Navigation: 'CONFIG' (Master Setup) vs 'DASHBOARD' (Earnings & Statement)
  const [activeTab, setActiveTab] = useState('CONFIG');

  // Master Data Catalogues
  const [doctors, setDoctors] = useState([]);
  const [centres, setCentres] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [investigations, setInvestigations] = useState([]);
  const [, setLoadingLookups] = useState(false);

  // Selected Scope (Doctor + Centre)
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [selectedCentreId, setSelectedCentreId] = useState('');
  const [settlementType, setSettlementType] = useState('MONTHLY'); // 'MONTHLY' | 'SPOT_CASH'

  // Commission Mode: 'DEPARTMENT' vs 'ITEM' (Strictly Mutually Exclusive)
  const [activeMode, setActiveMode] = useState('DEPARTMENT');
  const [existingConfig, setExistingConfig] = useState(null);
  const [, setLoadingConfig] = useState(false);
  const [saving, setSaving] = useState(false);

  // Mode Switch Confirmation Modal State
  const [switchModalOpen, setSwitchModalOpen] = useState(false);
  const [pendingModeSwitch, setPendingModeSwitch] = useState(null);

  // Department Rules State (keyed by departmentID)
  // { [deptId]: { type: 'PERCENTAGE'|'FIXED', rate: number, isActive: boolean } }
  const [deptRates, setDeptRates] = useState({});
  const [bulkDeptRate, setBulkDeptRate] = useState(10);
  const [bulkDeptType, setBulkDeptType] = useState('PERCENTAGE');

  // Item Rules State: array of items
  // [ { itemId, testCode, name, departmentName, rate, calculationType, sharePercent, shareAmount } ]
  const [itemRules, setItemRules] = useState([]);
  const [selectedItemToAdd, setSelectedItemToAdd] = useState('');
  const [newItemType, setNewItemType] = useState('FIXED');
  const [newItemRate, setNewItemRate] = useState('');

  // 1-Click Copy Tariff Modal State
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyForm, setCopyForm] = useState({
    fromDoctorId: '',
    fromCentreId: '',
    toDoctorId: '',
    toCentreId: '',
    copyMode: 'DEPARTMENT', // 'DEPARTMENT' | 'ITEM'
  });
  const [copying, setCopying] = useState(false);

  // Dashboard & Statement Report State
  const [dashFilters, setDashFilters] = useState({
    doctorId: '',
    centreId: '',
    payoutFilter: 'ALL', // 'ALL' | 'SPOT' | 'MONTHLY'
    dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0],
    search: '',
  });
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [loadingLedger, setLoadingLedger] = useState(false);

  // ─── 1. Load Reference Catalogues ───────────────────────────────────────────
  useEffect(() => {
    const loadCatalogs = async () => {
      setLoadingLookups(true);
      try {
        const [docsRes, ctrsRes, deptsRes, invsRes] = await Promise.all([
          masterService.getReferralDoctors({ isActive: true }),
          masterService.getCentres({ isActive: true }),
          masterService.getDepartments({ isActive: true }),
          masterService.getInvestigations({ isActive: true, pageSize: 500 }),
        ]);

        const docList = docsRes?.data || [];
        setDoctors(docList);
        setCentres(ctrsRes?.data || []);
        setDepartments(deptsRes?.data || []);
        setInvestigations(invsRes?.data?.items || invsRes?.data || []);

        // Default to first active doctor if available (for CONFIG editor tab only)
        if (docList.length > 0 && !selectedDoctorId) {
          const firstDocId = docList[0].doctorId || docList[0].doctor_ID;
          setSelectedDoctorId(firstDocId);
          // Note: dashFilters.doctorId remains '' (All Doctors Aggregated) so dashboard shows all transactions by default!
        }
      } catch (err) {
        showToast?.('Failed to load doctor & department masters.', 'error');
      } finally {
        setLoadingLookups(false);
      }
    };
    loadCatalogs();
  }, []);

  // ─── 2. Fetch Tariff Configuration for Selected Doctor & Centre ────────────
  const fetchDoctorConfig = useCallback(async (docId, centreId) => {
    if (!docId) return;
    setLoadingConfig(true);
    try {
      const res = await referralService.getShareConfigs({
        beneficiaryType: 'DOCTOR',
        beneficiaryId: docId,
        centreId: centreId || undefined,
      });

      const configs = res?.data || [];
      // Pick matching centre or global config
      const matched = configs.find(
        (c) => (centreId ? c.centreId === parseInt(centreId, 10) : !c.centreId) && c.isActive
      ) || configs[0] || null;

      if (matched) {
        // Fetch detailed rules for this configuration
        const detailRes = await referralService.getShareConfigById(matched.configId);
        const detailed = detailRes?.data || matched;
        setExistingConfig(detailed);

        // Detect settlement type from remarks
        if (detailed.remarks?.includes('SPOT_CASH')) {
          setSettlementType('SPOT_CASH');
        } else {
          setSettlementType('MONTHLY');
        }

        const rules = detailed.rules || [];
        const hasItemRules = rules.some((r) => r.ruleLevel === 'ITEM' && r.isActive);

        if (hasItemRules) {
          setActiveMode('ITEM');
          // Populate Item Rules
          const loadedItems = rules
            .filter((r) => r.ruleLevel === 'ITEM')
            .map((r) => ({
              ruleId: r.ruleId,
              itemId: r.itemId,
              testCode: r.itemCode || `TEST-${r.itemId}`,
              name: r.itemName || `Test #${r.itemId}`,
              departmentName: r.departmentName || 'General',
              rate: r.baseAmount || 0,
              calculationType: r.calculationType || 'FIXED',
              sharePercent: r.sharePercent || 0,
              shareAmount: r.shareAmount || 0,
              isActive: r.isActive !== false,
            }));
          setItemRules(loadedItems);
        } else {
          setActiveMode('DEPARTMENT');
          // Populate Department Rules
          const rateMap = {};
          rules.forEach((r) => {
            if (r.ruleLevel === 'DEPARTMENT' && r.departmentID) {
              rateMap[r.departmentID] = {
                type: r.calculationType || 'PERCENTAGE',
                rate: r.calculationType === 'FIXED' ? r.shareAmount : r.sharePercent,
                isActive: r.isActive !== false,
              };
            }
          });
          setDeptRates(rateMap);
        }
      } else {
        // No existing config: Clean slate
        setExistingConfig(null);
        setActiveMode('DEPARTMENT');
        setDeptRates({});
        setItemRules([]);
        setSettlementType('MONTHLY');
      }
    } catch (err) {
      console.error('Error fetching doctor tariff:', err);
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDoctorId) {
      fetchDoctorConfig(selectedDoctorId, selectedCentreId);
    }
  }, [selectedDoctorId, selectedCentreId, fetchDoctorConfig]);

  // Selected Doctor Object
  const currentDoctor = useMemo(() => {
    return doctors.find(
      (d) => String(d.doctorId || d.doctor_ID) === String(selectedDoctorId)
    );
  }, [doctors, selectedDoctorId]);

  // Selected Centre Object
  const currentCentre = useMemo(() => {
    if (!selectedCentreId) return null;
    return centres.find((c) => String(c.centreId || c.id) === String(selectedCentreId));
  }, [centres, selectedCentreId]);

  // ─── 3. Mutual Exclusivity Mode Switch Handling ────────────────────────────
  const handleModeSwitchRequest = (targetMode) => {
    if (targetMode === activeMode) return;

    // Check if current mode has active configured rules
    const hasActiveDeptRules =
      activeMode === 'DEPARTMENT' &&
      Object.values(deptRates).some((r) => r.rate > 0 && r.isActive !== false);

    const hasActiveItemRules =
      activeMode === 'ITEM' &&
      itemRules.some((r) => (r.sharePercent > 0 || r.shareAmount > 0) && r.isActive !== false);

    if (hasActiveDeptRules || hasActiveItemRules) {
      setPendingModeSwitch(targetMode);
      setSwitchModalOpen(true);
    } else {
      setActiveMode(targetMode);
    }
  };

  const confirmModeSwitch = () => {
    if (pendingModeSwitch) {
      setActiveMode(pendingModeSwitch);
      if (pendingModeSwitch === 'DEPARTMENT') {
        setItemRules([]);
      } else {
        setDeptRates({});
      }
    }
    setSwitchModalOpen(false);
    setPendingModeSwitch(null);
  };

  // ─── 4. Department Tariff Handlers ──────────────────────────────────────────
  const handleDeptRateChange = (deptId, field, value) => {
    setDeptRates((prev) => ({
      ...prev,
      [deptId]: {
        type: prev[deptId]?.type || 'PERCENTAGE',
        rate: prev[deptId]?.rate || 0,
        isActive: prev[deptId]?.isActive ?? true,
        [field]: value,
      },
    }));
  };

  const handleBulkApplyDepartments = () => {
    const val = parseFloat(bulkDeptRate) || 0;
    const newRates = {};
    departments.forEach((d) => {
      newRates[d.subCategoryId] = {
        type: bulkDeptType,
        rate: val,
        isActive: true,
      };
    });
    setDeptRates(newRates);
    showToast?.(`Applied ${bulkDeptType === 'PERCENTAGE' ? `${val}%` : `₹${val}`} to all ${departments.length} departments.`);
  };

  const handleSaveDepartmentTariff = async () => {
    if (!selectedDoctorId) {
      showToast?.('Please select a Referring Doctor first.', 'error');
      return;
    }

    setSaving(true);
    try {
      const activeRules = [];
      departments.forEach((dept) => {
        const conf = deptRates[dept.subCategoryId];
        if (conf && conf.rate > 0 && conf.isActive !== false) {
          activeRules.push({
            ruleLevel: 'DEPARTMENT',
            departmentID: dept.subCategoryId,
            calculationType: conf.type,
            calculationBase: 'NET_AMOUNT',
            sharePercent: conf.type === 'PERCENTAGE' ? parseFloat(conf.rate) : 0,
            shareAmount: conf.type === 'FIXED' ? parseFloat(conf.rate) : 0,
            priority: 3,
            isActive: true,
          });
        }
      });

      if (activeRules.length === 0) {
        showToast?.('Please specify a commission rate for at least one department.', 'error');
        setSaving(false);
        return;
      }

      const payload = {
        beneficiaryType: 'DOCTOR',
        beneficiaryId: parseInt(selectedDoctorId, 10),
        beneficiaryName: currentDoctor?.name || currentDoctor?.doctorName || 'Doctor',
        centreId: selectedCentreId ? parseInt(selectedCentreId, 10) : undefined,
        configName: `${currentDoctor?.name || 'Doctor'} - Dept Tariff ${selectedCentreId ? `(Centre ${selectedCentreId})` : '(Global)'}`,
        version: existingConfig?.version || 1,
        priority: 1,
        effectiveFrom: new Date().toISOString().split('T')[0],
        isActive: true,
        remarks: `SETTLEMENT:${settlementType} | DEPARTMENT_WISE_MODE`,
        rules: activeRules,
      };

      if (existingConfig && existingConfig.configId > 0) {
        await referralService.updateShareConfig(existingConfig.configId, payload);
        showToast?.(`Department tariff updated for Dr. ${payload.beneficiaryName}!`);
      } else {
        await referralService.createShareConfig(payload);
        showToast?.(`Department tariff created for Dr. ${payload.beneficiaryName}!`);
      }

      fetchDoctorConfig(selectedDoctorId, selectedCentreId);
    } catch (err) {
      showToast?.(err.response?.data?.message || 'Failed to save department tariff.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── 5. Item / Test Tariff Handlers ─────────────────────────────────────────
  const handleAddItemRule = () => {
    if (!selectedItemToAdd) {
      showToast?.('Please select a test / investigation to add.', 'error');
      return;
    }
    const rateVal = parseFloat(newItemRate);
    if (isNaN(rateVal) || rateVal <= 0) {
      showToast?.('Please enter a valid commission amount or percentage.', 'error');
      return;
    }

    const inv = investigations.find((i) => String(i.itemId) === String(selectedItemToAdd));
    if (!inv) return;

    if (itemRules.some((r) => String(r.itemId) === String(selectedItemToAdd))) {
      showToast?.(`"${inv.name}" is already in the tariff list. Edit its rate directly.`, 'warning');
      return;
    }

    const newRule = {
      itemId: inv.itemId,
      testCode: inv.testCode || `TEST-${inv.itemId}`,
      name: inv.name,
      departmentName: inv.subCategoryName || 'Pathology',
      rate: inv.rate || 0,
      calculationType: newItemType,
      sharePercent: newItemType === 'PERCENTAGE' ? rateVal : 0,
      shareAmount: newItemType === 'FIXED' ? rateVal : 0,
      isActive: true,
    };

    setItemRules((prev) => [newRule, ...prev]);
    setSelectedItemToAdd('');
    setNewItemRate('');
    showToast?.(`Added "${inv.name}" to tariff list.`);
  };

  const handleRemoveItemRule = (idx) => {
    setItemRules((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleUpdateItemRule = (idx, field, value) => {
    setItemRules((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const handleSaveItemTariff = async () => {
    if (!selectedDoctorId) {
      showToast?.('Please select a Referring Doctor first.', 'error');
      return;
    }

    if (itemRules.length === 0) {
      showToast?.('Please add at least one test to the item tariff list.', 'error');
      return;
    }

    setSaving(true);
    try {
      const activeRules = itemRules.map((r) => ({
        ruleLevel: 'ITEM',
        itemID: r.itemId,
        calculationType: r.calculationType,
        calculationBase: 'NET_AMOUNT',
        sharePercent: r.calculationType === 'PERCENTAGE' ? parseFloat(r.sharePercent) || 0 : 0,
        shareAmount: r.calculationType === 'FIXED' ? parseFloat(r.shareAmount) || 0 : 0,
        priority: 1,
        isActive: r.isActive !== false,
      }));

      const payload = {
        beneficiaryType: 'DOCTOR',
        beneficiaryId: parseInt(selectedDoctorId, 10),
        beneficiaryName: currentDoctor?.name || currentDoctor?.doctorName || 'Doctor',
        centreId: selectedCentreId ? parseInt(selectedCentreId, 10) : undefined,
        configName: `${currentDoctor?.name || 'Doctor'} - Item Tariff ${selectedCentreId ? `(Centre ${selectedCentreId})` : '(Global)'}`,
        version: existingConfig?.version || 1,
        priority: 1,
        effectiveFrom: new Date().toISOString().split('T')[0],
        isActive: true,
        remarks: `SETTLEMENT:${settlementType} | ITEM_WISE_MODE`,
        rules: activeRules,
      };

      if (existingConfig && existingConfig.configId > 0) {
        await referralService.updateShareConfig(existingConfig.configId, payload);
        showToast?.(`Item-wise tariff updated for Dr. ${payload.beneficiaryName}!`);
      } else {
        await referralService.createShareConfig(payload);
        showToast?.(`Item-wise tariff created for Dr. ${payload.beneficiaryName}!`);
      }

      fetchDoctorConfig(selectedDoctorId, selectedCentreId);
    } catch (err) {
      showToast?.(err.response?.data?.message || 'Failed to save item tariff.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── 6. 1-Click Copy Tariff Handler ─────────────────────────────────────────
  const handleOpenCopyModal = () => {
    setCopyForm({
      fromDoctorId: selectedDoctorId || (doctors[0]?.doctorId || ''),
      fromCentreId: selectedCentreId || '',
      toDoctorId: '',
      toCentreId: selectedCentreId || '',
      copyMode: activeMode,
    });
    setCopyModalOpen(true);
  };

  const handleExecuteCopy = async () => {
    if (!copyForm.toDoctorId) {
      showToast?.('Please choose the Target Doctor.', 'error');
      return;
    }
    if (copyForm.fromDoctorId === copyForm.toDoctorId && copyForm.fromCentreId === copyForm.toCentreId) {
      showToast?.('Source and Target doctor/centre cannot be identical.', 'warning');
      return;
    }

    const targetDoc = doctors.find((d) => String(d.doctorId || d.doctor_ID) === String(copyForm.toDoctorId));

    setCopying(true);
    try {
      const payload = {
        fromBeneficiaryType: 'DOCTOR',
        fromBeneficiaryId: parseInt(copyForm.fromDoctorId, 10),
        toBeneficiaryType: 'DOCTOR',
        toBeneficiaryId: parseInt(copyForm.toDoctorId, 10),
        toBeneficiaryName: targetDoc?.name || targetDoc?.doctorName || 'Target Doctor',
        centreId: copyForm.toCentreId ? parseInt(copyForm.toCentreId, 10) : undefined,
        copyMode: copyForm.copyMode, // 'DEPARTMENT' or 'ITEM'
      };

      const res = await referralService.copyShareConfig(payload);
      if (res?.success) {
        showToast?.(`Copied ${copyForm.copyMode.toLowerCase()} tariff rules to Dr. ${payload.toBeneficiaryName}!`);
        setCopyModalOpen(false);
        // Switch view to target doctor
        setSelectedDoctorId(copyForm.toDoctorId);
        setSelectedCentreId(copyForm.toCentreId);
      } else {
        showToast?.(res?.message || 'No rules found to copy.', 'warning');
      }
    } catch (err) {
      showToast?.(err.response?.data?.message || 'Failed to copy tariff.', 'error');
    } finally {
      setCopying(false);
    }
  };

  // ─── 7. Dashboard & Ledger Statement Loader ─────────────────────────────────
  const fetchLedger = useCallback(async () => {
    setLoadingLedger(true);
    try {
      const params = {
        beneficiaryType: 'DOCTOR',
        beneficiaryId: dashFilters.doctorId || undefined,
        centreId: dashFilters.centreId ? parseInt(dashFilters.centreId, 10) : undefined,
        startDate: dashFilters.dateFrom,
        endDate: dashFilters.dateTo,
      };

      const res = await referralService.getCommissionLedger(params);
      setLedgerEntries(res?.data || []);
    } catch (err) {
      console.error('Failed to load commission ledger:', err);
    } finally {
      setLoadingLedger(false);
    }
  }, [dashFilters]);

  useEffect(() => {
    if (activeTab === 'DASHBOARD') {
      fetchLedger();
    }
  }, [activeTab, fetchLedger]);

  // Filtered Ledger Rows
  const filteredLedger = useMemo(() => {
    return ledgerEntries.filter((item) => {
      if (dashFilters.search) {
        const q = dashFilters.search.toLowerCase();
        const match =
          item.patientName?.toLowerCase().includes(q) ||
          item.ledgerTransactionNo?.toLowerCase().includes(q) ||
          item.itemName?.toLowerCase().includes(q);
        if (!match) return false;
      }
      if (dashFilters.payoutFilter === 'SPOT') {
        return item.isSettled || item.settlementType === 'SPOT_CASH';
      }
      if (dashFilters.payoutFilter === 'MONTHLY') {
        return !item.isSettled && item.settlementType !== 'SPOT_CASH';
      }
      return true;
    });
  }, [ledgerEntries, dashFilters]);

  const ledgerPagination = usePagination({
    items: filteredLedger,
    resetDeps: [dashFilters],
  });

  // KPI Calculations
  const kpis = useMemo(() => {
    let grossBilling = 0;
    let totalComm = 0;
    let spotPaid = 0;
    const uniquePatientKeys = new Set();
    const uniqueVisitKeys = new Set();

    filteredLedger.forEach((row) => {
      grossBilling += Number(row.baseAmount || 0);
      const comm = Number(row.commissionAmount || 0);
      totalComm += comm;
      if (row.isSettled || row.settlementType === 'SPOT_CASH') {
        spotPaid += comm;
      }

      // Distinct Patient calculation (via Patient_ID or normalized Name)
      const pKey = row.patient_ID || row.patientId || (row.patientName ? row.patientName.trim().toUpperCase() : null);
      if (pKey) uniquePatientKeys.add(pKey);

      // Distinct Visit / Encounter calculation (via LedgerTransactionNo)
      if (row.ledgerTransactionNo) {
        uniqueVisitKeys.add(row.ledgerTransactionNo);
      }
    });

    const netPayable = Math.max(0, totalComm - spotPaid);

    return {
      uniquePatientCount: uniquePatientKeys.size,
      uniqueVisitCount: uniqueVisitKeys.size,
      totalTestsCount: filteredLedger.length,
      grossBilling,
      totalComm,
      spotPaid,
      netPayable,
    };
  }, [filteredLedger]);

  // Number to Indian Rupees Words Helper
  const numberToWordsRupees = (num) => {
    const n = Math.round(Number(num) || 0);
    if (n === 0) return 'Rupees Zero Only';
    const a = [
      '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'
    ];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const inWords = (val) => {
      if (val < 20) return a[val];
      if (val < 100) return b[Math.floor(val / 10)] + (val % 10 ? ' ' + a[val % 10] : '');
      if (val < 1000) return a[Math.floor(val / 100)] + ' Hundred' + (val % 100 ? ' and ' + inWords(val % 100) : '');
      if (val < 100000) return inWords(Math.floor(val / 1000)) + ' Thousand' + (val % 1000 ? ' ' + inWords(val % 1000) : '');
      if (val < 10000000) return inWords(Math.floor(val / 100000)) + ' Lakh' + (val % 100000 ? ' ' + inWords(val % 100000) : '');
      return inWords(Math.floor(val / 10000000)) + ' Crore' + (val % 10000000 ? ' ' + inWords(val % 10000000) : '');
    };

    return `Rupees ${inWords(n)} Only`;
  };

  // ─── 8. Comprehensive Print / Save as PDF Statement ─────────────────────────
  const handlePrintStatement = () => {
    if (filteredLedger.length === 0) {
      showToast?.('No commission transactions to print for the selected filter.', 'warning');
      return;
    }

    const selectedDocName = doctors.find((d) => String(d.doctorId || d.doctor_ID) === String(dashFilters.doctorId))?.name || 'All Doctors (Aggregated)';
    const selectedCentreName = centres.find((c) => String(c.centreId || c.id) === String(dashFilters.centreId))?.centreName || user?.centreName || 'Main Reference Lab';
    const centreAddress = user?.centreAddress || 'Central Laboratory Facility';
    const centreCode = user?.centreCode || 'AUR';
    const payoutFilterLabel = dashFilters.payoutFilter === 'SPOT' ? '⚡ Spot Cash Paid (At Counter)' : dashFilters.payoutFilter === 'MONTHLY' ? '📅 Monthly Credit Pending' : 'All Transactions';
    const wordsPayable = numberToWordsRupees(kpis.netPayable);

    const escapeHtml = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const rowsHtml = filteredLedger.map((item, idx) => {
      const rateStr = item.calculationType === 'FIXED'
        ? `Flat ₹${parseFloat(item.shareAmount ?? item.commissionAmount ?? 0).toFixed(2)}`
        : `${parseFloat(item.sharePercent || 0)}%`;
      const isSpot = item.isSettled || item.settlementType === 'SPOT_CASH';
      const isNewEncounter = idx === 0 || item.ledgerTransactionNo !== filteredLedger[idx - 1].ledgerTransactionNo;

      return `
        <tr style="${isNewEncounter && idx > 0 ? 'border-top: 2px solid #94a3b8;' : ''}">
          <td style="text-align: center; color: #64748b;">${idx + 1}</td>
          <td>
            <strong>${escapeHtml(item.ledgerTransactionNo)}</strong>
            <div style="font-size: 10px; color: #64748b;">${item.earnedDate ? new Date(item.earnedDate).toLocaleDateString() : '-'}</div>
          </td>
          <td>
            <strong>${escapeHtml(item.patientName || '-')}</strong>
            <div style="font-size: 10px; color: #64748b;">ID: ${escapeHtml(item.patient_ID || '-')}</div>
          </td>
          <td>${escapeHtml(item.beneficiaryName || '-')}</td>
          <td>
            <div>${escapeHtml(item.itemName)}</div>
            <div style="font-size: 10px; color: #64748b;">${escapeHtml(item.departmentName || '')}</div>
          </td>
          <td style="text-align: right;">₹${parseFloat(item.baseAmount || 0).toFixed(2)}</td>
          <td style="text-align: center;"><span style="background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-size: 11px;">${rateStr}</span></td>
          <td style="text-align: right; font-weight: 700; color: #047857;">₹${parseFloat(item.commissionAmount || 0).toFixed(2)}</td>
          <td style="text-align: center; font-size: 11px;">${isSpot ? '<span style="color: #166534; font-weight: 600;">⚡ Spot Cash</span>' : '<span style="color: #0369a1; font-weight: 600;">📅 Monthly</span>'}</td>
          <td style="text-align: center; font-size: 11px;">${item.isSettled ? '<span style="color: #15803d; font-weight: 600;">Settled</span>' : '<span style="color: #b45309; font-weight: 600;">Pending</span>'}</td>
        </tr>
      `;
    }).join('');

    const printHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Doctor Commission Statement - ${escapeHtml(selectedDocName)}</title>
        <style>
          @page {
            size: A4 landscape;
            margin: 10mm 12mm 12mm 12mm;
          }
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            font-size: 12px;
            color: #0f172a;
            margin: 0;
            padding: 16px;
            background: #fff;
          }
          .toolbar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: #1e293b;
            color: #fff;
            padding: 10px 16px;
            border-radius: 8px;
            margin-bottom: 16px;
          }
          .toolbar button {
            background: #0ea5e9;
            color: #fff;
            border: none;
            padding: 8px 16px;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 600;
            font-size: 13px;
          }
          .toolbar button:hover { background: #0284c7; }
          .toolbar .btn-close {
            background: #64748b;
            margin-left: 8px;
          }
          @media print {
            .toolbar { display: none !important; }
            body { padding: 0 !important; }
          }
          .header-box {
            border-bottom: 2px solid #0f172a;
            padding-bottom: 10px;
            margin-bottom: 12px;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
          }
          .lab-name {
            font-size: 20px;
            font-weight: 800;
            color: #0f172a;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .lab-sub {
            font-size: 11px;
            color: #475569;
            margin-top: 3px;
          }
          .doc-badge {
            text-align: right;
          }
          .doc-badge-title {
            font-size: 14px;
            font-weight: 800;
            color: #0284c7;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .meta-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 8px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            padding: 10px 12px;
            margin-bottom: 14px;
            font-size: 11px;
          }
          .meta-item strong { color: #334155; }
          .kpi-row {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            gap: 10px;
            margin-bottom: 16px;
          }
          .kpi-card {
            border: 1px solid #cbd5e1;
            border-radius: 6px;
            padding: 8px 10px;
            background: #ffffff;
          }
          .kpi-card.highlight {
            background: #f0fdf4;
            border-color: #86efac;
          }
          .kpi-card.warning {
            background: #fffbeb;
            border-color: #fde68a;
          }
          .kpi-label {
            font-size: 10px;
            font-weight: 700;
            text-transform: uppercase;
            color: #64748b;
          }
          .kpi-val {
            font-size: 16px;
            font-weight: 800;
            color: #0f172a;
            margin-top: 4px;
          }
          .kpi-sub {
            font-size: 9px;
            color: #64748b;
            margin-top: 2px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
            margin-bottom: 14px;
          }
          th {
            background: #0f172a;
            color: #ffffff;
            font-weight: 700;
            text-align: left;
            padding: 7px 8px;
            font-size: 11px;
          }
          td {
            padding: 6px 8px;
            border-bottom: 1px solid #e2e8f0;
          }
          tr:nth-child(even) {
            background: #f8fafc;
          }
          .totals-row td {
            background: #f1f5f9;
            font-weight: 800;
            border-top: 2px solid #0f172a;
            border-bottom: 2px solid #0f172a;
            font-size: 12px;
          }
          .words-box {
            background: #eff6ff;
            border: 1px solid #bfdbfe;
            border-radius: 6px;
            padding: 8px 12px;
            font-size: 11px;
            margin-bottom: 24px;
            color: #1e3a8a;
          }
          .signatures {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 24px;
            margin-top: 36px;
            page-break-inside: avoid;
          }
          .sig-line {
            border-top: 1px dashed #64748b;
            padding-top: 6px;
            text-align: center;
            font-size: 11px;
            font-weight: 600;
            color: #334155;
          }
        </style>
      </head>
      <body>
        <div class="toolbar">
          <div>
            <strong>Doctor Commission Statement & Payout Summary</strong> (${filteredLedger.length} Records)
          </div>
          <div>
            <button onclick="window.print()">🖨️ Print / Save as PDF</button>
            <button class="btn-close" onclick="window.close()">✕ Close</button>
          </div>
        </div>

        <div class="header-box">
          <div>
            <div class="lab-name">${escapeHtml(selectedCentreName)}</div>
            <div class="lab-sub">${escapeHtml(centreAddress)} • Code: ${escapeHtml(centreCode)} • NABL Accredited Diagnostic Network</div>
          </div>
          <div class="doc-badge">
            <div class="doc-badge-title">COMMISSION STATEMENT</div>
            <div style="font-size: 10px; color: #64748b; margin-top: 2px;">Generated: ${new Date().toLocaleString()}</div>
          </div>
        </div>

        <div class="meta-grid">
          <div class="meta-item"><strong>Referring Doctor:</strong> ${escapeHtml(selectedDocName)}</div>
          <div class="meta-item"><strong>Centre Scope:</strong> ${escapeHtml(selectedCentreName)}</div>
          <div class="meta-item"><strong>Billing Period:</strong> ${escapeHtml(dashFilters.dateFrom)} to ${escapeHtml(dashFilters.dateTo)}</div>
          <div class="meta-item"><strong>Payout Mode:</strong> ${escapeHtml(payoutFilterLabel)}</div>
        </div>

        <div class="kpi-row">
          <div class="kpi-card">
            <div class="kpi-label">Patient Referrals</div>
            <div class="kpi-val">${kpis.uniquePatientCount}</div>
            <div class="kpi-sub">${kpis.totalTestsCount} Tests (${kpis.uniqueVisitCount} Encounters)</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Gross Billing</div>
            <div class="kpi-val">₹${kpis.grossBilling.toFixed(2)}</div>
            <div class="kpi-sub">Total referred test value</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Total Commission</div>
            <div class="kpi-val">₹${kpis.totalComm.toFixed(2)}</div>
            <div class="kpi-sub">Calculated earnings</div>
          </div>
          <div class="kpi-card warning">
            <div class="kpi-label">⚡ Spot Cash Paid</div>
            <div class="kpi-val">₹${kpis.spotPaid.toFixed(2)}</div>
            <div class="kpi-sub">Deducted at counter</div>
          </div>
          <div class="kpi-card highlight">
            <div class="kpi-label">Net Balance Due</div>
            <div class="kpi-val" style="color: #047857;">₹${kpis.netPayable.toFixed(2)}</div>
            <div class="kpi-sub">Accounts payable voucher</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 35px; text-align: center;">#</th>
              <th>Date / Lab No</th>
              <th>Patient Name</th>
              <th>Doctor Name</th>
              <th>Investigation / Department</th>
              <th style="text-align: right;">Bill Amt</th>
              <th style="text-align: center;">Applied Rate</th>
              <th style="text-align: right;">Commission</th>
              <th style="text-align: center;">Payout Mode</th>
              <th style="text-align: center;">Status</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
            <tr class="totals-row">
              <td colspan="5" style="text-align: right;">GRAND TOTALS (${kpis.uniquePatientCount} Patient / ${kpis.totalTestsCount} Tests):</td>
              <td style="text-align: right;">₹${kpis.grossBilling.toFixed(2)}</td>
              <td></td>
              <td style="text-align: right; color: #047857;">₹${kpis.totalComm.toFixed(2)}</td>
              <td style="text-align: center; font-size: 10px;">Spot: ₹${kpis.spotPaid.toFixed(2)}</td>
              <td style="text-align: center; color: #047857;">Due: ₹${kpis.netPayable.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>

        <div class="words-box">
          <strong>Net Payable Amount in Words:</strong> ${wordsPayable}
        </div>

        <div class="signatures">
          <div class="sig-line">Prepared By (Billing Desk)</div>
          <div class="sig-line">Verified & Approved By (Finance)</div>
          <div class="sig-line">Referring Doctor Acknowledgment</div>
        </div>
      </body>
      </html>
    `;

    const printWin = window.open('', '_blank', 'width=1100,height=850,menubar=no,toolbar=no,location=no');
    if (printWin) {
      printWin.document.open();
      printWin.document.write(printHtml);
      printWin.document.close();
      printWin.focus();
      setTimeout(() => {
        printWin.print();
      }, 500);
    } else {
      window.print();
    }
  };

  // ─── 9. Comprehensive Export to Excel / CSV ─────────────────────────────────
  const handleExportExcel = () => {
    if (filteredLedger.length === 0) {
      showToast?.('No commission transactions to export for the selected filter.', 'warning');
      return;
    }

    const selectedDocName = doctors.find((d) => String(d.doctorId || d.doctor_ID) === String(dashFilters.doctorId))?.name || 'All Doctors (Aggregated)';
    const selectedCentreName = centres.find((c) => String(c.centreId || c.id) === String(dashFilters.centreId))?.centreName || user?.centreName || 'All Diagnostic Centres';
    const payoutFilterLabel = dashFilters.payoutFilter === 'SPOT' ? 'Spot Cash Paid (Counter)' : dashFilters.payoutFilter === 'MONTHLY' ? 'Monthly Credit Pending' : 'All Transactions';

    const csvRows = [];
    csvRows.push(['ENTERPRISE LIMS - REFERRAL COMMISSION & DOCTOR PAYOUT STATEMENT']);
    csvRows.push(['Facility / Lab:', user?.centreName || 'Main Reference Lab']);
    csvRows.push(['Referring Doctor:', selectedDocName]);
    csvRows.push(['Diagnostic Centre:', selectedCentreName]);
    csvRows.push(['Date Range:', `${dashFilters.dateFrom} to ${dashFilters.dateTo}`]);
    csvRows.push(['Payout Mode Filter:', payoutFilterLabel]);
    csvRows.push(['Exported On:', new Date().toLocaleString()]);
    csvRows.push(['Exported By:', user?.employeeName || user?.username || 'System Administrator']);
    csvRows.push([]);

    // KPI Summary
    csvRows.push(['FINANCIAL SUMMARY KPI', '']);
    csvRows.push(['Unique Patients Referred', kpis.uniquePatientCount]);
    csvRows.push(['Total Tests Referred', kpis.totalTestsCount]);
    csvRows.push(['Total Encounters / Visits', kpis.uniqueVisitCount]);
    csvRows.push(['Gross Lab Billing (INR)', kpis.grossBilling.toFixed(2)]);
    csvRows.push(['Total Commission Earned (INR)', kpis.totalComm.toFixed(2)]);
    csvRows.push(['Spot Cash Paid at Counter (INR)', kpis.spotPaid.toFixed(2)]);
    csvRows.push(['Net Balance Due (INR)', kpis.netPayable.toFixed(2)]);
    csvRows.push([]);

    // Table Column Headers
    csvRows.push([
      'Sr No',
      'Lab No / Visit',
      'Date',
      'Patient Name',
      'Patient ID',
      'Referring Doctor',
      'Investigation / Test Name',
      'Department',
      'Bill Amount (INR)',
      'Applied Rate',
      'Commission Amount (INR)',
      'Payout Mode',
      'Settlement Status'
    ]);

    // Data Rows
    filteredLedger.forEach((item, index) => {
      const rateStr = item.calculationType === 'FIXED'
        ? `Flat INR ${parseFloat(item.shareAmount ?? item.commissionAmount ?? 0).toFixed(2)}`
        : `${parseFloat(item.sharePercent || 0)}%`;

      const payoutStr = item.isSettled || item.settlementType === 'SPOT_CASH' ? 'Spot Cash Paid' : 'Monthly Credit';
      const statusStr = item.isSettled ? 'Settled' : 'Pending';

      csvRows.push([
        index + 1,
        item.ledgerTransactionNo || '',
        item.earnedDate ? new Date(item.earnedDate).toLocaleDateString() : '',
        item.patientName || '',
        item.patient_ID || item.patientId || '',
        item.beneficiaryName || '',
        item.itemName || '',
        item.departmentName || '',
        parseFloat(item.baseAmount || 0).toFixed(2),
        rateStr,
        parseFloat(item.commissionAmount || 0).toFixed(2),
        payoutStr,
        statusStr
      ]);
    });

    // Grand Totals Row
    csvRows.push([]);
    csvRows.push([
      'TOTAL',
      '',
      '',
      `${kpis.uniquePatientCount} Patient(s)`,
      '',
      '',
      `${kpis.totalTestsCount} Test(s)`,
      '',
      kpis.grossBilling.toFixed(2),
      '',
      kpis.totalComm.toFixed(2),
      `Spot: ${kpis.spotPaid.toFixed(2)} | Net Due: ${kpis.netPayable.toFixed(2)}`,
      ''
    ]);

    const csvContent = '\uFEFF' + csvRows.map((row) =>
      row.map((cell) => {
        const val = cell === null || cell === undefined ? '' : String(cell);
        if (val.includes(',') || val.includes('"') || val.includes('\n')) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      }).join(',')
    ).join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeDoc = selectedDocName.replace(/[^a-zA-Z0-9]/g, '_');
    link.setAttribute('href', url);
    link.setAttribute('download', `Commission_Statement_${safeDoc}_${dashFilters.dateFrom}_to_${dashFilters.dateTo}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast?.('Commission Statement exported to Excel successfully!');
  };

  return (
    <MasterPageWrapper
      masterTitle="Referral Doctor Tariff & Payout Master"
      masterName="Doctor Commission Rules"
      viewMode="form"
      actions={
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            className="ref-btn ref-btn-secondary"
            onClick={handleOpenCopyModal}
          >
            📋 Copy Tariff to Doctor
          </button>
          <button
            type="button"
            className="ref-btn ref-btn-secondary"
            onClick={() => {
              if (activeTab === 'CONFIG') fetchDoctorConfig(selectedDoctorId, selectedCentreId);
              else fetchLedger();
            }}
          >
            🔄 Refresh
          </button>
        </div>
      }
    >
      <div className="ref-master-root">
        {/* Navigation Tabs */}
        <div className="ref-nav-tabs">
          <button
            type="button"
            className={`ref-nav-tab ${activeTab === 'CONFIG' ? 'active' : ''}`}
            onClick={() => setActiveTab('CONFIG')}
          >
            ⚙️ Commission Tariff Master
          </button>
          <button
            type="button"
            className={`ref-nav-tab ${activeTab === 'DASHBOARD' ? 'active' : ''}`}
            onClick={() => setActiveTab('DASHBOARD')}
          >
            📊 Doctor Commission Dashboard & Statement
          </button>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════
            TAB 1: COMMISSION TARIFF CONFIGURATION
            ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'CONFIG' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Scope Selection Bar */}
            <div className="ref-scope-bar">
              <div className="ref-scope-grid">
                {/* Centre Selector */}
                <div className="ref-field-group">
                  <label className="ref-field-label">Diagnostic Centre</label>
                  <select
                    className="ref-field-select"
                    value={selectedCentreId}
                    onChange={(e) => setSelectedCentreId(e.target.value)}
                  >
                    <option value="">🌐 Global (All Diagnostic Centres)</option>
                    {centres.map((c) => {
                      const cId = c.centreId || c.id;
                      return (
                        <option key={cId} value={cId}>
                          🏥 {c.centreName || c.name} {c.centreCode ? `(${c.centreCode})` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Doctor Selector */}
                <div className="ref-field-group">
                  <label className="ref-field-label">Referring Doctor *</label>
                  <select
                    className="ref-field-select"
                    value={selectedDoctorId}
                    onChange={(e) => setSelectedDoctorId(e.target.value)}
                  >
                    <option value="">-- Choose Referring Doctor --</option>
                    {doctors.map((d) => {
                      const dId = d.doctorId || d.doctor_ID;
                      return (
                        <option key={dId} value={dId}>
                          {d.name || d.doctorName} [{d.doctorCode || 'DOC'}] • {d.specialization || 'Consultant'}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Settlement Type Toggle */}
                <div className="ref-field-group">
                  <label className="ref-field-label">Settlement Policy</label>
                  <div className="ref-settlement-toggle">
                    <button
                      type="button"
                      className={`ref-settle-opt ${settlementType === 'MONTHLY' ? 'active monthly' : ''}`}
                      onClick={() => setSettlementType('MONTHLY')}
                    >
                      📅 Monthly Credit
                    </button>
                    <button
                      type="button"
                      className={`ref-settle-opt ${settlementType === 'SPOT_CASH' ? 'active spot' : ''}`}
                      onClick={() => setSettlementType('SPOT_CASH')}
                      title="Instant cash payout at registration counter with petty cash expense voucher deduction"
                    >
                      ⚡ Spot Cash Payout
                    </button>
                  </div>
                </div>

                {/* Quick Copy Trigger */}
                <div className="ref-field-group" style={{ justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="ref-btn ref-btn-secondary"
                    onClick={handleOpenCopyModal}
                    title="Copy tariff from or to another doctor"
                  >
                    📋 Copy Tariff
                  </button>
                </div>
              </div>

              {/* Doctor Details Ribbon */}
              {currentDoctor && (
                <div className="ref-doc-ribbon">
                  <div>
                    <strong>Dr. {currentDoctor.name || currentDoctor.doctorName}</strong>
                    <span style={{ margin: '0 8px', color: '#cbd5e1' }}>|</span>
                    <span style={{ color: '#64748b' }}>Code: {currentDoctor.doctorCode || 'DOC'}</span>
                    <span style={{ margin: '0 8px', color: '#cbd5e1' }}>|</span>
                    <span style={{ color: '#64748b' }}>Spec: {currentDoctor.specialization || 'General'}</span>
                    <span style={{ margin: '0 8px', color: '#cbd5e1' }}>|</span>
                    <span>Centre Scope: <strong>{currentCentre ? currentCentre.centreName : 'Global (All Centres)'}</strong></span>
                  </div>
                  <div>
                    {existingConfig ? (
                      <span style={{ background: '#ecfdf5', color: '#047857', padding: '4px 10px', borderRadius: '12px', fontWeight: 700, fontSize: '0.8rem' }}>
                        ✓ Active Tariff: {activeMode === 'DEPARTMENT' ? 'Department-Wise' : 'Item-Wise'} (v{existingConfig.version || 1})
                      </span>
                    ) : (
                      <span style={{ background: '#fef3c7', color: '#92400e', padding: '4px 10px', borderRadius: '12px', fontWeight: 600, fontSize: '0.8rem' }}>
                        ⚠️ Not Configured Yet (Defaulting to 15% Standard Fallback)
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Mode Switcher Cards (Strictly Mutually Exclusive) */}
            <div className="ref-mode-selection">
              <div
                className={`ref-mode-card ${activeMode === 'DEPARTMENT' ? 'selected' : ''}`}
                onClick={() => handleModeSwitchRequest('DEPARTMENT')}
              >
                <div className="ref-mode-icon">🔬</div>
                <div className="ref-mode-content">
                  <h3>Department-Wise Commission</h3>
                  <p>
                    Flat percentage or fixed amount applied to all tests inside a department
                    (e.g., Biochemistry 10%, Haematology 15%, Radiology ₹100 flat).
                  </p>
                </div>
                {activeMode === 'DEPARTMENT' && <span className="ref-mode-badge">ACTIVE MODE</span>}
              </div>

              <div
                className={`ref-mode-card ${activeMode === 'ITEM' ? 'selected' : ''}`}
                onClick={() => handleModeSwitchRequest('ITEM')}
              >
                <div className="ref-mode-icon">🧪</div>
                <div className="ref-mode-content">
                  <h3>Item / Test-Wise Commission</h3>
                  <p>
                    Specific customized commission rate per test (e.g., CBC ₹50 flat,
                    LFT ₹100 flat, Lipid Profile 15%).
                  </p>
                </div>
                {activeMode === 'ITEM' && <span className="ref-mode-badge">ACTIVE MODE</span>}
              </div>
            </div>

            {/* Mutual Exclusivity Rule Note */}
            <div className="ref-exclusive-alert">
              <div>
                <strong>⚠️ Strict Lab Policy:</strong> A doctor can only have <strong>one active tariff mode</strong> at a time.
                {activeMode === 'DEPARTMENT'
                  ? ' Currently operating in Department-Wise mode. All tests under configured departments earn department rates.'
                  : ' Currently operating in Item-Wise mode. Only explicitly configured individual tests earn commissions.'}
              </div>
              <span style={{ fontSize: '0.78rem', background: '#ffffff', padding: '4px 8px', borderRadius: '6px', border: '1px solid #fde68a', fontWeight: 700 }}>
                Mutual Exclusivity Enforced
              </span>
            </div>

            {/* ═══════════════════════════════════════════════════════════════════
                MODE A: DEPARTMENT-WISE TARIFF EDITOR
                ═══════════════════════════════════════════════════════════════════ */}
            {activeMode === 'DEPARTMENT' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Fast Bulk Apply Bar */}
                <div className="ref-bulk-bar">
                  <div className="ref-bulk-left">
                    <div className="ref-bulk-title">
                      <span className="ref-bulk-icon">⚡</span>
                      <span>Fast Bulk Apply to All Departments:</span>
                    </div>
                    <div className="ref-bulk-inputs">
                      <div className="ref-input-with-symbol">
                        <span className="ref-symbol-prefix">{bulkDeptType === 'PERCENTAGE' ? '%' : '₹'}</span>
                        <input
                          type="number"
                          min="0"
                          max={bulkDeptType === 'PERCENTAGE' ? 100 : 10000}
                          step="0.5"
                          className="ref-rate-input ref-rate-input-bulk"
                          placeholder="Rate"
                          value={bulkDeptRate}
                          onChange={(e) => setBulkDeptRate(e.target.value)}
                        />
                      </div>
                      <div className="ref-type-btn-group">
                        <button
                          type="button"
                          className={`ref-type-btn ${bulkDeptType === 'PERCENTAGE' ? 'active' : ''}`}
                          onClick={() => setBulkDeptType('PERCENTAGE')}
                        >
                          % Percentage
                        </button>
                        <button
                          type="button"
                          className={`ref-type-btn ${bulkDeptType === 'FIXED' ? 'active' : ''}`}
                          onClick={() => setBulkDeptType('FIXED')}
                        >
                          ₹ Flat Amount
                        </button>
                      </div>
                      <button
                        type="button"
                        className="ref-btn ref-btn-bulk-apply"
                        onClick={handleBulkApplyDepartments}
                      >
                        Apply to All
                      </button>
                    </div>
                  </div>
                  <div className="ref-dept-count-badge">
                    Total Departments: <strong>{departments.length}</strong>
                  </div>
                </div>

                {/* Department Rate Table */}
                <div className="ref-table-wrapper">
                  <table className="ref-table">
                    <thead>
                      <tr>
                        <th style={{ width: '80px' }}>Dept ID</th>
                        <th>Department Name</th>
                        <th style={{ width: '180px' }}>Commission Type</th>
                        <th style={{ width: '160px', textAlign: 'right' }}>Commission Rate</th>
                        <th style={{ width: '120px', textAlign: 'center' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departments.length === 0 ? (
                        <tr>
                          <td colSpan={5} style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                            Loading departments...
                          </td>
                        </tr>
                      ) : (
                        departments.map((dept) => {
                          const conf = deptRates[dept.subCategoryId] || {
                            type: 'PERCENTAGE',
                            rate: '',
                            isActive: true,
                          };
                          const hasRate = conf.rate !== '' && parseFloat(conf.rate) > 0;
                          return (
                            <tr key={dept.subCategoryId}>
                              <td>
                                <span style={{ color: '#64748b', fontWeight: 600 }}>#{dept.subCategoryId}</span>
                              </td>
                              <td>
                                <strong style={{ color: '#0f172a' }}>{dept.name}</strong>
                                {dept.code && <span style={{ color: '#94a3b8', marginLeft: '6px' }}>({dept.code})</span>}
                              </td>
                              <td>
                                <div className="ref-type-btn-group">
                                  <button
                                    type="button"
                                    className={`ref-type-btn ${conf.type === 'PERCENTAGE' ? 'active' : ''}`}
                                    onClick={() => handleDeptRateChange(dept.subCategoryId, 'type', 'PERCENTAGE')}
                                  >
                                    % Percent
                                  </button>
                                  <button
                                    type="button"
                                    className={`ref-type-btn ${conf.type === 'FIXED' ? 'active' : ''}`}
                                    onClick={() => handleDeptRateChange(dept.subCategoryId, 'type', 'FIXED')}
                                  >
                                    ₹ Flat
                                  </button>
                                </div>
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.5"
                                  placeholder="0.00"
                                  className="ref-rate-input"
                                  value={conf.rate}
                                  onChange={(e) => handleDeptRateChange(dept.subCategoryId, 'rate', e.target.value)}
                                />
                                <span style={{ marginLeft: '6px', fontWeight: 700, color: hasRate ? '#059669' : '#94a3b8' }}>
                                  {conf.type === 'PERCENTAGE' ? '%' : '₹'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={conf.isActive !== false}
                                    onChange={(e) => handleDeptRateChange(dept.subCategoryId, 'isActive', e.target.checked)}
                                  />
                                  <span style={{ marginLeft: '6px', fontSize: '0.8rem', color: conf.isActive !== false ? '#059669' : '#94a3b8' }}>
                                    {conf.isActive !== false ? 'Active' : 'Off'}
                                  </span>
                                </label>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Save Department Tariff Button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
                  <button
                    type="button"
                    className="ref-btn ref-btn-success"
                    onClick={handleSaveDepartmentTariff}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : '💾 Save Department Commission Tariff'}
                  </button>
                </div>
              </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════════
                MODE B: ITEM / TEST-WISE TARIFF EDITOR
                ═══════════════════════════════════════════════════════════════════ */}
            {activeMode === 'ITEM' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Search & Add Test Bar */}
                <div className="ref-item-add-bar">
                  <div style={{ flex: 2, minWidth: '260px' }}>
                    <select
                      className="ref-field-select"
                      style={{ width: '100%' }}
                      value={selectedItemToAdd}
                      onChange={(e) => setSelectedItemToAdd(e.target.value)}
                    >
                      <option value="">🔍 Choose Test / Investigation to Add Rate...</option>
                      {investigations.map((i) => (
                        <option key={i.itemId} value={i.itemId}>
                          [{i.testCode || 'TEST'}] {i.name} • MRP: ₹{parseFloat(i.rate || 0).toFixed(2)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="ref-type-btn-group">
                    <button
                      type="button"
                      className={`ref-type-btn ${newItemType === 'FIXED' ? 'active' : ''}`}
                      onClick={() => setNewItemType('FIXED')}
                    >
                      ₹ Flat Amount
                    </button>
                    <button
                      type="button"
                      className={`ref-type-btn ${newItemType === 'PERCENTAGE' ? 'active' : ''}`}
                      onClick={() => setNewItemType('PERCENTAGE')}
                    >
                      % Percentage
                    </button>
                  </div>

                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    placeholder={newItemType === 'FIXED' ? 'Rate (₹)' : 'Share (%)'}
                    className="ref-rate-input"
                    value={newItemRate}
                    onChange={(e) => setNewItemRate(e.target.value)}
                  />

                  <button
                    type="button"
                    className="ref-btn ref-btn-primary"
                    onClick={handleAddItemRule}
                  >
                    + Add Test Rate
                  </button>
                </div>

                {/* Configured Item Rates Table */}
                <div className="ref-table-wrapper">
                  <table className="ref-table">
                    <thead>
                      <tr>
                        <th style={{ width: '110px' }}>Test Code</th>
                        <th>Investigation / Test Name</th>
                        <th>Department</th>
                        <th style={{ width: '120px', textAlign: 'right' }}>Lab MRP</th>
                        <th style={{ width: '160px' }}>Commission Type</th>
                        <th style={{ width: '140px', textAlign: 'right' }}>Commission Rate</th>
                        <th style={{ width: '140px', textAlign: 'right' }}>Calculated Net</th>
                        <th style={{ width: '60px', textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemRules.length === 0 ? (
                        <tr>
                          <td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                            No individual test rates configured yet for this doctor.
                            <br />
                            <small>Use the selector above to add specific test commissions.</small>
                          </td>
                        </tr>
                      ) : (
                        itemRules.map((rule, idx) => {
                          const isPct = rule.calculationType === 'PERCENTAGE';
                          const calcAmt = isPct
                            ? ((rule.rate || 0) * (parseFloat(rule.sharePercent) || 0)) / 100
                            : parseFloat(rule.shareAmount) || 0;

                          return (
                            <tr key={idx}>
                              <td>
                                <strong style={{ color: '#0284c7' }}>{rule.testCode}</strong>
                              </td>
                              <td>
                                <strong style={{ color: '#0f172a' }}>{rule.name}</strong>
                              </td>
                              <td style={{ color: '#64748b' }}>{rule.departmentName}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600 }}>
                                ₹{parseFloat(rule.rate || 0).toFixed(2)}
                              </td>
                              <td>
                                <div className="ref-type-btn-group">
                                  <button
                                    type="button"
                                    className={`ref-type-btn ${rule.calculationType === 'FIXED' ? 'active' : ''}`}
                                    onClick={() => handleUpdateItemRule(idx, 'calculationType', 'FIXED')}
                                  >
                                    ₹ Flat
                                  </button>
                                  <button
                                    type="button"
                                    className={`ref-type-btn ${rule.calculationType === 'PERCENTAGE' ? 'active' : ''}`}
                                    onClick={() => handleUpdateItemRule(idx, 'calculationType', 'PERCENTAGE')}
                                  >
                                    % Pct
                                  </button>
                                </div>
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.5"
                                  className="ref-rate-input"
                                  value={isPct ? rule.sharePercent : rule.shareAmount}
                                  onChange={(e) =>
                                    handleUpdateItemRule(
                                      idx,
                                      isPct ? 'sharePercent' : 'shareAmount',
                                      parseFloat(e.target.value) || 0
                                    )
                                  }
                                />
                                <span style={{ marginLeft: '6px', fontWeight: 700, color: '#059669' }}>
                                  {isPct ? '%' : '₹'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 700, color: '#059669' }}>
                                ₹{calcAmt.toFixed(2)}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <button
                                  type="button"
                                  className="ref-btn-outline-danger"
                                  onClick={() => handleRemoveItemRule(idx)}
                                >
                                  ✕
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Save Item Tariff Button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
                  <button
                    type="button"
                    className="ref-btn ref-btn-success"
                    onClick={handleSaveItemTariff}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : '💾 Save Item-Wise Commission Tariff'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            TAB 2: DOCTOR COMMISSION DASHBOARD & STATEMENT REPORT
            ═══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'DASHBOARD' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Filter Bar */}
            <div className="ref-scope-bar">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', alignItems: 'flex-end' }}>
                <div className="ref-field-group">
                  <label className="ref-field-label">Referring Doctor</label>
                  <select
                    className="ref-field-select"
                    value={dashFilters.doctorId}
                    onChange={(e) => setDashFilters((prev) => ({ ...prev, doctorId: e.target.value }))}
                  >
                    <option value="">All Doctors (Aggregated)</option>
                    {doctors.map((d) => (
                      <option key={d.doctorId || d.doctor_ID} value={d.doctorId || d.doctor_ID}>
                        {d.name || d.doctorName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ref-field-group">
                  <label className="ref-field-label">Centre</label>
                  <select
                    className="ref-field-select"
                    value={dashFilters.centreId}
                    onChange={(e) => setDashFilters((prev) => ({ ...prev, centreId: e.target.value }))}
                  >
                    <option value="">All Centres</option>
                    {centres.map((c) => (
                      <option key={c.centreId || c.id} value={c.centreId || c.id}>
                        {c.centreName || c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ref-field-group">
                  <label className="ref-field-label">Payout Mode Filter</label>
                  <select
                    className="ref-field-select"
                    value={dashFilters.payoutFilter}
                    onChange={(e) => setDashFilters((prev) => ({ ...prev, payoutFilter: e.target.value }))}
                  >
                    <option value="ALL">All Transactions</option>
                    <option value="SPOT">⚡ Spot Cash Paid (At Counter)</option>
                    <option value="MONTHLY">📅 Monthly Credit Pending</option>
                  </select>
                </div>

                <div className="ref-field-group">
                  <label className="ref-field-label">Date From</label>
                  <input
                    type="date"
                    className="ref-field-input"
                    value={dashFilters.dateFrom}
                    onChange={(e) => setDashFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                  />
                </div>

                <div className="ref-field-group">
                  <label className="ref-field-label">Date To</label>
                  <input
                    type="date"
                    className="ref-field-input"
                    value={dashFilters.dateTo}
                    onChange={(e) => setDashFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                  />
                </div>

                <div className="ref-field-group" style={{ justifyContent: 'flex-end', flexDirection: 'row', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="ref-btn ref-btn-secondary"
                    onClick={handleExportExcel}
                    title="Export statement to Excel / CSV spreadsheet"
                    style={{ background: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0' }}
                  >
                    📊 Export to Excel
                  </button>
                  <button
                    type="button"
                    className="ref-btn ref-btn-secondary"
                    onClick={handlePrintStatement}
                    title="Print or Save as PDF Doctor Commission Statement"
                  >
                    🖨️ Print Statement
                  </button>
                  <button
                    type="button"
                    className="ref-btn ref-btn-primary"
                    onClick={fetchLedger}
                  >
                    🔍 Filter
                  </button>
                </div>
              </div>
            </div>

            {/* KPI Cards */}
            <div className="ref-kpi-row">
              <div className="ref-dash-kpi">
                <div className="ref-dash-kpi-title">Patient Referrals</div>
                <div className="ref-dash-kpi-value">{kpis.uniquePatientCount}</div>
                <div className="ref-dash-kpi-sub">
                  {kpis.totalTestsCount} test{kpis.totalTestsCount === 1 ? '' : 's'} referred across {kpis.uniqueVisitCount} encounter{kpis.uniqueVisitCount === 1 ? '' : 's'}
                </div>
              </div>

              <div className="ref-dash-kpi">
                <div className="ref-dash-kpi-title">Gross Lab Billing</div>
                <div className="ref-dash-kpi-value">₹{kpis.grossBilling.toFixed(2)}</div>
                <div className="ref-dash-kpi-sub">Patient test value generated</div>
              </div>

              <div className="ref-dash-kpi purple">
                <div className="ref-dash-kpi-title">Total Commission Earned</div>
                <div className="ref-dash-kpi-value">₹{kpis.totalComm.toFixed(2)}</div>
                <div className="ref-dash-kpi-sub">Calculated via active rules</div>
              </div>

              <div className="ref-dash-kpi warning">
                <div className="ref-dash-kpi-title">⚡ Spot Cash Paid (Counter)</div>
                <div className="ref-dash-kpi-value">₹{kpis.spotPaid.toFixed(2)}</div>
                <div className="ref-dash-kpi-sub">Deducted from cashier collection</div>
              </div>

              <div className="ref-dash-kpi success">
                <div className="ref-dash-kpi-title">Net Balance Due</div>
                <div className="ref-dash-kpi-value">₹{kpis.netPayable.toFixed(2)}</div>
                <div className="ref-dash-kpi-sub">Payable via accounts voucher</div>
              </div>
            </div>

            {/* Commission Ledger Table */}
            <div className="ref-table-wrapper" id="printable-statement">
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1rem', color: '#0f172a' }}>
                    Referral Commission Transaction Ledger
                  </h3>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>
                    Showing {filteredLedger.length} tests across {kpis.uniquePatientCount} distinct patient{kpis.uniquePatientCount === 1 ? '' : 's'} ({kpis.uniqueVisitCount} encounter{kpis.uniqueVisitCount === 1 ? '' : 's'})
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="Search patient, lab no, test..."
                  className="ref-field-input"
                  style={{ width: '280px', height: '36px' }}
                  value={dashFilters.search}
                  onChange={(e) => setDashFilters((prev) => ({ ...prev, search: e.target.value }))}
                />
              </div>

              <table className="ref-table">
                <thead>
                  <tr>
                    <th>Date / Lab No</th>
                    <th>Patient Name</th>
                    <th>Doctor Name</th>
                    <th>Test / Department</th>
                    <th style={{ textAlign: 'right' }}>Bill Amt</th>
                    <th style={{ textAlign: 'center' }}>Applied Rate</th>
                    <th style={{ textAlign: 'right' }}>Commission</th>
                    <th style={{ textAlign: 'center' }}>Payout Mode</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingLedger ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                        Loading transaction ledger...
                      </td>
                    </tr>
                  ) : ledgerPagination.paginatedItems.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                        No commission transactions recorded for the selected filter.
                      </td>
                    </tr>
                  ) : (
                    ledgerPagination.paginatedItems.map((item, idx) => {
                      const isNewEncounter = idx === 0 || item.ledgerTransactionNo !== ledgerPagination.paginatedItems[idx - 1].ledgerTransactionNo;
                      return (
                        <tr
                          key={idx}
                          style={isNewEncounter && idx > 0 ? { borderTop: '2px solid #cbd5e1' } : undefined}
                        >
                          <td>
                            <div style={{ fontWeight: 700, color: '#0f172a' }}>{item.ledgerTransactionNo}</div>
                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                              {item.earnedDate ? new Date(item.earnedDate).toLocaleDateString() : '-'}
                            </div>
                          </td>
                          <td>
                            <strong>{item.patientName}</strong>
                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>ID: {item.patient_ID || '-'}</div>
                          </td>
                          <td>
                            <div style={{ fontWeight: 600, color: '#1e293b' }}>{item.beneficiaryName}</div>
                          </td>
                          <td>
                            <div style={{ fontWeight: 500, color: '#0f172a' }}>{item.itemName}</div>
                            <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{item.departmentName}</div>
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>
                            ₹{parseFloat(item.baseAmount || 0).toFixed(2)}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span style={{ background: '#f1f5f9', padding: '3px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600, color: '#334155' }}>
                              {item.calculationType === 'FIXED'
                                ? `Flat ₹${parseFloat(item.shareAmount ?? item.commissionAmount ?? 0).toFixed(2)}`
                                : `${parseFloat(item.sharePercent || 0)}%`}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: '#059669', fontSize: '0.95rem' }}>
                            ₹{parseFloat(item.commissionAmount || 0).toFixed(2)}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {item.isSettled || item.settlementType === 'SPOT_CASH' ? (
                              <span style={{ background: '#dcfce7', color: '#166534', padding: '3px 8px', borderRadius: '12px', fontSize: '0.72rem', fontWeight: 700 }}>
                                ⚡ Spot Cash Paid
                              </span>
                            ) : (
                              <span style={{ background: '#e0f2fe', color: '#075985', padding: '3px 8px', borderRadius: '12px', fontSize: '0.72rem', fontWeight: 700 }}>
                                📅 Monthly Credit
                              </span>
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span style={{ background: item.isSettled ? '#f0fdf4' : '#fffbeb', color: item.isSettled ? '#15803d' : '#b45309', padding: '2px 8px', borderRadius: '12px', fontSize: '0.72rem', fontWeight: 700 }}>
                              {item.isSettled ? 'Settled' : 'Pending'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>

              <CommonPagination
                page={ledgerPagination.page}
                pageSize={ledgerPagination.pageSize}
                totalRecords={ledgerPagination.totalRecords}
                onPageChange={ledgerPagination.setPage}
                onPageSizeChange={ledgerPagination.setPageSize}
                loading={loadingLedger}
              />
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            MODAL 1: MUTUAL EXCLUSIVITY CONFIRMATION
            ═══════════════════════════════════════════════════════════════════════ */}
        {switchModalOpen && (
          <div className="ref-dialog-overlay">
            <div className="ref-dialog-box">
              <div className="ref-dialog-header">
                <h3>⚠️ Switch Commission Master Mode</h3>
                <button
                  type="button"
                  style={{ background: 'transparent', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}
                  onClick={() => setSwitchModalOpen(false)}
                >
                  ✕
                </button>
              </div>
              <div className="ref-dialog-body">
                <p>
                  <strong>Dr. {currentDoctor?.name || 'Selected Doctor'}</strong> currently has active{' '}
                  <span style={{ color: '#0284c7', fontWeight: 700 }}>
                    {activeMode === 'DEPARTMENT' ? 'Department-Wise' : 'Item-Wise'}
                  </span>{' '}
                  commission rules.
                </p>
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px', margin: '14px 0', fontSize: '0.85rem', color: '#92400e' }}>
                  <strong>Operational Rule:</strong> A doctor cannot have both Department-wise and Item-wise
                  commission simultaneously. Switching will replace the current rules with{' '}
                  <strong>{pendingModeSwitch === 'DEPARTMENT' ? 'Department-Wise' : 'Item-Wise'}</strong> mode upon saving.
                </div>
                <p style={{ margin: 0, fontSize: '0.86rem', color: '#64748b' }}>
                  Are you sure you want to proceed and configure{' '}
                  <strong>{pendingModeSwitch === 'DEPARTMENT' ? 'Department-Wise' : 'Item-Wise'}</strong> tariff?
                </p>
              </div>
              <div className="ref-dialog-footer">
                <button
                  type="button"
                  className="ref-btn ref-btn-secondary"
                  onClick={() => setSwitchModalOpen(false)}
                >
                  Cancel (Keep Current Mode)
                </button>
                <button
                  type="button"
                  className="ref-btn ref-btn-primary"
                  onClick={confirmModeSwitch}
                >
                  Confirm & Switch Mode
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            MODAL 2: 1-CLICK COPY / CLONE TARIFF
            ═══════════════════════════════════════════════════════════════════════ */}
        {copyModalOpen && (
          <div className="ref-dialog-overlay">
            <div className="ref-dialog-box">
              <div className="ref-dialog-header">
                <h3>📋 1-Click Copy Tariff Configuration</h3>
                <button
                  type="button"
                  style={{ background: 'transparent', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}
                  onClick={() => setCopyModalOpen(false)}
                >
                  ✕
                </button>
              </div>
              <div className="ref-dialog-body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>
                  Instantly duplicate department or item commission rules from one doctor to another.
                </p>

                {/* Source Doctor */}
                <div className="ref-field-group">
                  <label className="ref-field-label">Copy From (Source Doctor)</label>
                  <select
                    className="ref-field-select"
                    value={copyForm.fromDoctorId}
                    onChange={(e) => setCopyForm((prev) => ({ ...prev, fromDoctorId: e.target.value }))}
                  >
                    <option value="">-- Choose Source Doctor --</option>
                    {doctors.map((d) => (
                      <option key={d.doctorId || d.doctor_ID} value={d.doctorId || d.doctor_ID}>
                        {d.name || d.doctorName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Target Doctor */}
                <div className="ref-field-group">
                  <label className="ref-field-label">Copy To (Target Doctor) *</label>
                  <select
                    className="ref-field-select"
                    value={copyForm.toDoctorId}
                    onChange={(e) => setCopyForm((prev) => ({ ...prev, toDoctorId: e.target.value }))}
                  >
                    <option value="">-- Choose Target Doctor --</option>
                    {doctors.map((d) => (
                      <option key={d.doctorId || d.doctor_ID} value={d.doctorId || d.doctor_ID}>
                        {d.name || d.doctorName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Target Centre Scope */}
                <div className="ref-field-group">
                  <label className="ref-field-label">Target Centre Scope</label>
                  <select
                    className="ref-field-select"
                    value={copyForm.toCentreId}
                    onChange={(e) => setCopyForm((prev) => ({ ...prev, toCentreId: e.target.value }))}
                  >
                    <option value="">🌐 Global (All Centres)</option>
                    {centres.map((c) => (
                      <option key={c.centreId || c.id} value={c.centreId || c.id}>
                        🏥 {c.centreName || c.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Copy Mode Radio Options */}
                <div className="ref-field-group">
                  <label className="ref-field-label">Copy Mode Selection *</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.88rem' }}>
                      <input
                        type="radio"
                        name="copyMode"
                        value="DEPARTMENT"
                        checked={copyForm.copyMode === 'DEPARTMENT'}
                        onChange={(e) => setCopyForm((prev) => ({ ...prev, copyMode: e.target.value }))}
                      />
                      <span><strong>Copy Department-Wise Rules</strong> (Flat % / flat ₹ per department)</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.88rem' }}>
                      <input
                        type="radio"
                        name="copyMode"
                        value="ITEM"
                        checked={copyForm.copyMode === 'ITEM'}
                        onChange={(e) => setCopyForm((prev) => ({ ...prev, copyMode: e.target.value }))}
                      />
                      <span><strong>Copy Item-Wise Rules</strong> (Individual test rates like CBC, LFT, etc.)</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="ref-dialog-footer">
                <button
                  type="button"
                  className="ref-btn ref-btn-secondary"
                  onClick={() => setCopyModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="ref-btn ref-btn-primary"
                  onClick={handleExecuteCopy}
                  disabled={copying}
                >
                  {copying ? 'Copying...' : 'Execute 1-Click Copy'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MasterPageWrapper>
  );
};

export default ReferralShareConfigPage;
